// =============================================================================
// Delete Organization Dialog - Conferma eliminazione definitiva (solo OWNER)
// =============================================================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  refreshMyMemberships,
  useDeleteTenant,
  useTenantDeletionSummary,
} from '../../api/hooks';
import { useTranslatedError } from '../../hooks/useTranslatedError';
import { formatDate } from '../../lib/utils';
import { useTenantStore } from '../../store/tenantStore';
import { useToast } from '../../store/toastStore';
import { Button, Feedback, FormField, LoadingBlock, TextInput } from './_shared';

interface Props {
  organizationName: string;
  onClose: () => void;
}

const FORM_ID = 'delete-organization-form';

/**
 * Eliminazione dell'organizzazione. Due sicurezze prima della conferma: i
 * conteggi di cosa viene disattivato (letti dal server al momento) e la
 * password dell'owner, perche' una sessione aperta non e' una conferma.
 *
 * L'eliminazione e' un soft delete: l'organizzazione diventa subito
 * inaccessibile ma resta ripristinabile fino alla data di purge, che il
 * server restituisce e che mostriamo nel messaggio di conferma.
 *
 * Dopo il successo il tenant attivo non e' piu' utilizzabile: si azzera lo
 * store e la cache delle query (contengono dati dell'organizzazione eliminata)
 * e si esce verso la selezione organizzazione o l'onboarding.
 */
export function DeleteOrganizationDialog({ organizationName, onClose }: Props) {
  const { t } = useTranslation('settings');
  const { getErrorMessage } = useTranslatedError();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteTenant = useDeleteTenant();
  // Disabilitata durante e dopo l'eliminazione: senza questo, lo svuotamento
  // della cache farebbe ripartire la query verso un'organizzazione che non
  // esiste piu' (richiesta destinata a fallire, con l'header gia' rimosso).
  const { data: summary, isLoading } = useTenantDeletionSummary(
    !deleteTenant.isPending && !deleteTenant.isSuccess,
  );

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!password) return;
    setError(null);

    try {
      const result = await deleteTenant.mutateAsync(password);

      // Da qui in poi il tenant attivo non esiste piu': prima si smette di
      // parlarne (header x-tenant-id, cache), poi si naviga.
      useTenantStore.getState().clearTenant();
      queryClient.clear();

      const memberships = await refreshMyMemberships();
      toast.success(
        t('organization.danger.deleted', { name: result.name }),
        t('organization.danger.deletedHint', {
          date: formatDate(result.purgeAt),
        }),
      );

      navigate(memberships.length > 0 ? '/select-tenant' : '/onboarding', {
        replace: true,
      });
    } catch (err) {
      setError(getErrorMessage(err, t('organization.danger.error')));
    }
  };

  const rows: { key: string; value: number }[] = summary
    ? [
        { key: 'members', value: summary.members },
        { key: 'pendingInvites', value: summary.pendingInvites },
        { key: 'customers', value: summary.customers },
        { key: 'resources', value: summary.resources },
        { key: 'services', value: summary.services },
        { key: 'appointments', value: summary.appointments },
        { key: 'bookingRequests', value: summary.bookingRequests },
      ].filter((r) => r.value > 0)
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-100 text-red-600">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {t('organization.danger.dialogTitle')}
              </h2>
              <p className="text-sm text-gray-500 truncate">{organizationName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:buttons.close')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto space-y-5">
          <p className="text-sm text-gray-600">
            {t('organization.danger.dialogIntro', { name: organizationName })}
          </p>

          {isLoading && <LoadingBlock label={t('organization.danger.loadingSummary')} />}

          {summary && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-xs font-medium text-red-700 uppercase tracking-wide mb-2">
                {t('organization.danger.summaryTitle')}
              </p>
              {rows.length > 0 ? (
                <ul className="space-y-1">
                  {rows.map(({ key, value }) => (
                    <li key={key} className="flex justify-between text-sm text-red-800">
                      <span>{t(`organization.danger.entities.${key}`)}</span>
                      <span className="font-semibold tabular-nums">{value}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-red-800">{t('organization.danger.summaryEmpty')}</p>
              )}
            </div>
          )}

          <p className="text-sm text-gray-600">{t('organization.danger.irreversible')}</p>

          <p className="text-sm text-gray-500">{t('organization.danger.restorable')}</p>

          {/* autoComplete="off" sul form + "current-password" sull'input: il
              password manager propone la password giusta senza offrirsi di
              salvarne una nuova. */}
          <form
            id={FORM_ID}
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              void handleDelete();
            }}
          >
            <FormField label={t('organization.danger.passwordLabel')} required>
              <TextInput
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('organization.danger.passwordPlaceholder')}
              />
            </FormField>
          </form>

          {error && <Feedback type="error" message={error} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={deleteTenant.isPending}>
            {t('common:buttons.cancel')}
          </Button>
          {/* Il bottone sta nel footer, fuori dal form: `form` + type submit
              lo ricollega, cosi' anche Invio dal campo password conferma. */}
          <Button
            type="submit"
            form={FORM_ID}
            variant="danger"
            loading={deleteTenant.isPending}
            disabled={!password}
          >
            {t('organization.danger.confirmButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
